import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import { 
    searchUsers, 
    sendFriendRequest, 
    getFriendRequests, 
    acceptFriendRequest, 
    rejectFriendRequest,
    getFriends,
    removeFriend
} from '../services/api';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const FriendsModal = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'search', 'requests'
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [friends, setFriends] = useState([]);
    const [friendRequests, setFriendRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (isOpen && user) {
            if (activeTab === 'friends') {
                fetchFriends();
            } else if (activeTab === 'requests') {
                fetchFriendRequests();
            }
        }
    }, [isOpen, user, activeTab]);

    const fetchFriends = async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            const data = await getFriends(token);
            setFriends(data);
        } catch (err) {
            console.error('Error fetching friends:', err);
            setError('Failed to load friends');
        } finally {
            setLoading(false);
        }
    };

    const fetchFriendRequests = async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            const data = await getFriendRequests('received', token);
            setFriendRequests(data);
        } catch (err) {
            console.error('Error fetching friend requests:', err);
            setError('Failed to load friend requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!user || !searchQuery.trim() || searchQuery.trim().length < 2) {
            setError('Please enter at least 2 characters to search');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            const results = await searchUsers(searchQuery, token);
            setSearchResults(results);
        } catch (err) {
            console.error('Error searching users:', err);
            setError(err.message || 'Failed to search users');
        } finally {
            setLoading(false);
        }
    };

    const handleSendRequest = async (receiverId) => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        try {
            const token = await user.getIdToken();
            await sendFriendRequest(receiverId, null, token);
            setSuccessMessage('Friend request sent!');
            
            // Refresh search results
            await handleSearch();
        } catch (err) {
            console.error('Error sending friend request:', err);
            setError(err.message || 'Failed to send friend request');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptRequest = async (requestId) => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        try {
            const token = await user.getIdToken();
            await acceptFriendRequest(requestId, token);
            setSuccessMessage('Friend request accepted!');
            
            // Refresh friend requests
            await fetchFriendRequests();
        } catch (err) {
            console.error('Error accepting friend request:', err);
            setError(err.message || 'Failed to accept friend request');
        } finally {
            setLoading(false);
        }
    };

    const handleRejectRequest = async (requestId) => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        try {
            const token = await user.getIdToken();
            await rejectFriendRequest(requestId, token);
            setSuccessMessage('Friend request rejected');
            
            // Refresh friend requests
            await fetchFriendRequests();
        } catch (err) {
            console.error('Error rejecting friend request:', err);
            setError(err.message || 'Failed to reject friend request');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveFriend = async (friendId) => {
        if (!user) return;
        
        if (!window.confirm('Are you sure you want to remove this friend?')) {
            return;
        }
        
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        try {
            const token = await user.getIdToken();
            await removeFriend(friendId, token);
            setSuccessMessage('Friend removed');
            
            // Refresh friends list
            await fetchFriends();
        } catch (err) {
            console.error('Error removing friend:', err);
            setError(err.message || 'Failed to remove friend');
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        if (activeTab === 'friends') {
            return (
                <div className="space-y-4">
                    {loading ? (
                        <Text className="text-center">Loading friends...</Text>
                    ) : friends.length === 0 ? (
                        <Text className="text-center opacity-60">No friends yet. Search for users to add friends!</Text>
                    ) : (
                        <div className="space-y-2">
                            {friends.map(friend => (
                                <div 
                                    key={friend.id} 
                                    className="flex items-center justify-between p-3 bg-neutral-50 hairline"
                                >
                                    <div>
                                        <Text className="font-medium">{friend.display_name || friend.username}</Text>
                                        <Text className="text-sm opacity-60">@{friend.username}</Text>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveFriend(friend.id)}
                                        className="px-2 py-1 text-xs bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 font-mono uppercase tracking-wider transition-all"
                                        disabled={loading}
                                    >
                                        REMOVE
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        if (activeTab === 'search') {
            return (
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder="Search by username or email..."
                            className="flex-1 px-3 py-2 hairline bg-neutral-0 font-sans"
                        />
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-all"
                            style={{ backgroundColor: colorTokens.oxfordBlue, color: colorTokens.white }}
                            disabled={loading}
                        >
                            SEARCH
                        </button>
                    </div>

                    {loading ? (
                        <Text className="text-center">Searching...</Text>
                    ) : searchResults.length === 0 ? (
                        searchQuery && <Text className="text-center opacity-60">No users found</Text>
                    ) : (
                        <div className="space-y-2">
                            {searchResults.map(result => (
                                <div 
                                    key={result.id} 
                                    className="flex items-center justify-between p-3 bg-neutral-50 hairline"
                                >
                                    <div>
                                        <Text className="font-medium">{result.display_name || result.username}</Text>
                                        <Text className="text-sm opacity-60">@{result.username}</Text>
                                    </div>
                                    <div>
                                        {result.relationship_status === 'friends' && (
                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider opacity-60">
                                                FRIENDS
                                            </span>
                                        )}
                                        {result.relationship_status === 'request_sent' && (
                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider opacity-60">
                                                REQUEST SENT
                                            </span>
                                        )}
                                        {result.relationship_status === 'request_received' && (
                                            <span className="px-2 py-1 text-xs font-mono uppercase tracking-wider opacity-60">
                                                REQUEST RECEIVED
                                            </span>
                                        )}
                                        {result.relationship_status === 'none' && (
                                            <button
                                                onClick={() => handleSendRequest(result.id)}
                                                className="px-2 py-1 text-xs hover:opacity-60 font-mono uppercase tracking-wider transition-all"
                                                style={{ backgroundColor: colorTokens.oxfordBlue, color: colorTokens.white }}
                                                disabled={loading}
                                            >
                                                ADD FRIEND
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        if (activeTab === 'requests') {
            return (
                <div className="space-y-4">
                    {loading ? (
                        <Text className="text-center">Loading requests...</Text>
                    ) : friendRequests.length === 0 ? (
                        <Text className="text-center opacity-60">No pending friend requests</Text>
                    ) : (
                        <div className="space-y-2">
                            {friendRequests.map(request => (
                                <div 
                                    key={request.id} 
                                    className="p-3 bg-neutral-50 hairline space-y-2"
                                >
                                    <div>
                                        <Text className="font-medium">{request.sender_display_name || request.sender_username}</Text>
                                        <Text className="text-sm opacity-60">@{request.sender_username}</Text>
                                        {request.message && (
                                            <Text className="text-sm mt-1 italic">"{request.message}"</Text>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleAcceptRequest(request.id)}
                                            className="px-2 py-1 text-xs hover:opacity-60 font-mono uppercase tracking-wider transition-all"
                                            style={{ backgroundColor: colorTokens.oxfordBlue, color: colorTokens.white }}
                                            disabled={loading}
                                        >
                                            ACCEPT
                                        </button>
                                        <button
                                            onClick={() => handleRejectRequest(request.id)}
                                            className="px-2 py-1 text-xs bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 font-mono uppercase tracking-wider transition-all"
                                            disabled={loading}
                                        >
                                            REJECT
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="FRIENDS">
            <div className="space-y-4">
                {/* Tabs */}
                <div className="flex gap-2 hairline-b pb-2">
                    <button
                        onClick={() => setActiveTab('friends')}
                        className={`px-3 py-1 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'friends' 
                                ? 'opacity-100 font-bold' 
                                : 'opacity-60 hover:opacity-100'
                        }`}
                        style={{ color: colorTokens.neutral[900] }}
                    >
                        MY FRIENDS
                    </button>
                    <button
                        onClick={() => setActiveTab('search')}
                        className={`px-3 py-1 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'search' 
                                ? 'opacity-100 font-bold' 
                                : 'opacity-60 hover:opacity-100'
                        }`}
                        style={{ color: colorTokens.neutral[900] }}
                    >
                        SEARCH
                    </button>
                    <button
                        onClick={() => setActiveTab('requests')}
                        className={`px-3 py-1 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'requests' 
                                ? 'opacity-100 font-bold' 
                                : 'opacity-60 hover:opacity-100'
                        }`}
                        style={{ color: colorTokens.neutral[900] }}
                    >
                        REQUESTS
                        {friendRequests.length > 0 && (
                            <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">
                                {friendRequests.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="p-3 bg-red-50 hairline">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}
                {successMessage && (
                    <div className="p-3 bg-green-50 hairline">
                        <Text className="text-green-600">{successMessage}</Text>
                    </div>
                )}

                {/* Tab Content */}
                <div className="max-h-96 overflow-y-auto">
                    {renderTabContent()}
                </div>
            </div>
        </Modal>
    );
};

export default FriendsModal;

